import React, { useRef, useMemo } from 'react';
import {
  Box,
  Grid,
  TextField,
  Typography,
  IconButton,
  FormHelperText,
} from '@mui/material';
import {
  CloudUpload,
  Delete,
} from '@mui/icons-material';

const formInputStyle = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    transition: 'all 0.2s ease-in-out',
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#7522D1',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#7522D1',
      borderWidth: '2px',
    },
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#7522D1',
  },
};

const PersonalInformation = ({ formData, handleInputChange, handleFileUpload, handleRemoveLogo, fileInputRef, errors = {}, touched = {} }) => {
  const validateField = (fieldName, value) => {
    const validators = {
      companyName: (val) => {
        if (!val || val.trim().length < 2) return 'Company name must be at least 2 characters';
        if (val.length > 100) return 'Company name must not exceed 100 characters';
        return null;
      },
      legalEntityName: (val) => {
        if (!val || val.trim().length < 2) return 'Legal entity name must be at least 2 characters';
        if (val.length > 100) return 'Legal entity name must not exceed 100 characters';
        return null;
      },
      emailAddress: (val) => {
        if (val && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(val)) {
          return 'Please enter a valid email address';
        }
        return null;
      },
      phoneNumber: (val) => {
        if (val && !/^[\+]?[1-9][\d]{0,15}$/.test(val.replace(/[\s\-\(\)]/g, ''))) {
          return 'Please enter a valid phone number';
        }
        return null;
      },
      websiteUrl: (val) => {
        if (val && !/^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)$/.test(val)) {
          return 'Please enter a valid website URL';
        }
        return null;
      },
      postalCode: (val) => {
        if (val && !/^[A-Za-z0-9\s\-]{3,10}$/.test(val)) {
          return 'Please enter a valid postal code';
        }
        return null;
      },
      taxIdentificationNumber: (val) => {
        if (val && !/^[A-Za-z0-9]{8,20}$/.test(val)) {
          return 'Tax identification number must be 8-20 alphanumeric characters';
        }
        return null;
      }
    };

    return validators[fieldName] ? validators[fieldName](value) : null;
  };

  const fieldErrors = useMemo(() => {
    const errors = {};
    Object.keys(formData).forEach(key => {
      if (touched[key]) {
        const error = validateField(key, formData[key]);
        if (error) errors[key] = error;
      }
    });
    return errors;
  }, [formData, touched]);

  return (
    <Grid container spacing={3}>
      <Grid item size={{ xs: 12, md: 6 }}>
        <TextField
          fullWidth
          label="Company Name"
          value={formData.companyName}
          onChange={(e) => handleInputChange('companyName', e.target.value)}
          variant="outlined"
          size="small"
          required
          error={!!errors.companyName || !!fieldErrors.companyName}
          helperText={errors.companyName || fieldErrors.companyName || ' '}
          sx={formInputStyle}
        />
      </Grid>
      <Grid item size={{ xs: 12, md: 6 }}>
        <TextField
          fullWidth
          label="Legal Entity Name"
          value={formData.legalEntityName}
          onChange={(e) => handleInputChange('legalEntityName', e.target.value)}
          variant="outlined"
          size="small"
          required
          error={!!errors.legalEntityName || !!fieldErrors.legalEntityName}
          helperText={errors.legalEntityName || fieldErrors.legalEntityName || ' '}
          sx={formInputStyle}
        />
      </Grid>

      <Grid item size={{ xs: 12 }}>
        <Typography variant="subtitle2" sx={{ mb: 1.5, color: '#030007', fontWeight: 700 }}>
          Company Logo
        </Typography>
        <Box
          sx={{
            border: '2px dashed rgba(117, 34, 209, 0.3)',
            borderRadius: '16px',
            p: 4,
            textAlign: 'center',
            backgroundColor: '#F9FAFB',
            cursor: 'pointer',
            transition: 'all 0.25s ease-in-out',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            '&:hover': {
              borderColor: '#7522D1',
              backgroundColor: '#F5EEFC',
              transform: 'scale(1.005)',
              boxShadow: '0 4px 20px rgba(117, 34, 209, 0.05)',
            }
          }}
          onClick={() => fileInputRef.current?.click()}
        >
          {formData.companyLogo ? (
            <Box sx={{ position: 'relative', display: 'inline-block' }}>
              <img
                src={typeof formData.companyLogo === 'string' ? formData.companyLogo : URL.createObjectURL(formData.companyLogo)}
                alt="Company Logo"
                style={{ maxWidth: '200px', maxHeight: '150px', objectFit: 'contain', borderRadius: '8px' }}
              />
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveLogo();
                }}
                sx={{
                  position: 'absolute',
                  top: -10,
                  right: -10,
                  backgroundColor: '#FFFFFF',
                  color: '#EF4444',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  border: '1px solid rgba(0,0,0,0.05)',
                  '&:hover': {
                    backgroundColor: '#FEF2F2',
                    color: '#B91C1C',
                  }
                }}
              >
                <Delete sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          ) : (
            <Box>
              <CloudUpload sx={{ fontSize: 48, color: '#7522D1', mb: 2 }} />
              <Typography variant="body2" sx={{ color: '#4B5563', mb: 1, fontWeight: 600 }}>
                Drag and drop your logo here or click to browse
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Supported formats: PNG, JPG, GIF (Max 4MB)
              </Typography>
              {errors.companyLogo && (
                <FormHelperText error sx={{ mt: 1, textAlign: 'center', fontWeight: 600 }}>
                  {errors.companyLogo}
                </FormHelperText>
              )}
            </Box>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
          />
        </Box>
      </Grid>

      <Grid item size={{ xs: 12 }}>
        <TextField
          fullWidth
          label="Address"
          value={formData.address}
          onChange={(e) => handleInputChange('address', e.target.value)}
          variant="outlined"
          size="small"
          multiline
          rows={2}
          inputProps={{ maxLength: 500 }}
          helperText={`${formData.address?.length || 0}/500`}
          sx={formInputStyle}
        />
      </Grid>

      <Grid item size={{ xs: 12, md: 4 }}>
        <TextField
          fullWidth
          label="Country"
          value={formData.country}
          onChange={(e) => handleInputChange('country', e.target.value)}
          variant="outlined"
          size="small"
          inputProps={{ maxLength: 50 }}
          sx={formInputStyle}
        />
      </Grid>
      <Grid item size={{ xs: 12, md: 4 }}>
        <TextField
          fullWidth
          label="City"
          value={formData.city}
          onChange={(e) => handleInputChange('city', e.target.value)}
          variant="outlined"
          size="small"
          inputProps={{ maxLength: 50 }}
          sx={formInputStyle}
        />
      </Grid>
      <Grid item size={{ xs: 12, md: 4 }}>
        <TextField
          fullWidth
          label="State/Province"
          value={formData.state}
          onChange={(e) => handleInputChange('state', e.target.value)}
          variant="outlined"
          size="small"
          inputProps={{ maxLength: 50 }}
          sx={formInputStyle}
        />
      </Grid>

      <Grid item size={{ xs: 12, md: 6 }}>
        <TextField
          fullWidth
          label="Postal Code"
          value={formData.postalCode}
          onChange={(e) => handleInputChange('postalCode', e.target.value)}
          variant="outlined"
          size="small"
          error={!!errors.postalCode || !!fieldErrors.postalCode}
          helperText={errors.postalCode || fieldErrors.postalCode || ' '}
          sx={formInputStyle}
        />
      </Grid>
      <Grid item size={{ xs: 12, md: 6 }}>
        <TextField
          fullWidth
          label="Phone Number"
          value={formData.phoneNumber}
          onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
          variant="outlined"
          size="small"
          placeholder="+1 (555) 123-4567"
          error={!!errors.phoneNumber || !!fieldErrors.phoneNumber}
          helperText={errors.phoneNumber || fieldErrors.phoneNumber || ' '}
          sx={formInputStyle}
        />
      </Grid>

      <Grid item size={{ xs: 12, md: 6 }}>
        <TextField
          fullWidth
          label="Email Address"
          value={formData.emailAddress}
          onChange={(e) => handleInputChange('emailAddress', e.target.value)}
          variant="outlined"
          size="small"
          type="email"
          placeholder="company@example.com"
          error={!!errors.emailAddress || !!fieldErrors.emailAddress}
          helperText={errors.emailAddress || fieldErrors.emailAddress || ' '}
          sx={formInputStyle}
        />
      </Grid>
      <Grid item size={{ xs: 12, md: 6 }}>
        <TextField
          fullWidth
          label="Website URL"
          value={formData.websiteUrl}
          onChange={(e) => handleInputChange('websiteUrl', e.target.value)}
          variant="outlined"
          size="small"
          placeholder="https://www.example.com"
          error={!!errors.websiteUrl || !!fieldErrors.websiteUrl}
          helperText={errors.websiteUrl || fieldErrors.websiteUrl || ' '}
          sx={formInputStyle}
        />
      </Grid>

      <Grid item size={{ xs: 12, md: 6 }}>
        <TextField
          fullWidth
          label="Tax Identification Number"
          value={formData.taxIdentificationNumber}
          onChange={(e) => handleInputChange('taxIdentificationNumber', e.target.value)}
          variant="outlined"
          size="small"
          placeholder="XX-XXXXXXX"
          error={!!errors.taxIdentificationNumber || !!fieldErrors.taxIdentificationNumber}
          helperText={errors.taxIdentificationNumber || fieldErrors.taxIdentificationNumber || ' '}
          sx={formInputStyle}
        />
      </Grid>
      <Grid item size={{ xs: 12, md: 6 }}>
        <TextField
          fullWidth
          label="Industry Type"
          value={formData.industryType}
          onChange={(e) => handleInputChange('industryType', e.target.value)}
          variant="outlined"
          size="small"
          inputProps={{ maxLength: 50 }}
          sx={formInputStyle}
        />
      </Grid>
    </Grid>
  );
};

export default PersonalInformation;
